import { apiClient } from './apiClient';

export interface ExchangeTokenRequest {
  supabase_token: string;
  tenant_id?: string;
  invitation_token?: string;
  device_info?: Record<string, any>;
}

export interface ExchangeTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user?: Record<string, any>;
  tenants?: Array<Record<string, any>>;
}

export interface UserMeResponse {
  id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  role?: string | null;
  tenant?: any | null;
  active_site?: any | null;
  session_id?: string | null;
  permissions?: Record<string, any> | null;
  is_active?: boolean | null;
  // Legacy fields for backward compatibility
  full_name?: string | null;
  tenant_id?: string | null;
  site_id?: string | null;
}

export interface Site {
  id: string;
  name: string;
  code: string;
  created_at: string;
  updated_at: string;
}

export interface SetActiveSiteRequest {
  site_id: string;
}

export interface CreateOrganizationRequest {
  name: string;
  admin_email: string;
  support_email?: string;
}

export interface CreateOrganizationResponse {
  organization_id: string;
  name: string;
  admin_email: string;
  support_email?: string;
  message?: string;
}

export interface MyOrganization {
  id: string;
  name: string;
  admin_email: string;
  support_email?: string;
  created_at: string;
  updated_at: string;
}

export interface MyInvitation {
  id: string;
  tenant_id: string;
  email: string;
  role: string;
  invited_by: string;
  created_at: string;
  expires_at: string;
  accepted_at?: string;
  accepted_by?: string;
}

export interface AcceptInvitationRequest {
  invitation_token: string;
}

export const authApi = apiClient.injectEndpoints({
  endpoints: (builder) => ({
    updateAccount: builder.mutation<UserMeResponse, Partial<Pick<UserMeResponse, 'first_name' | 'last_name' | 'phone' | 'role'>>>({
      query: (body) => ({
        url: '/api/v1/auth/account',
        method: 'PATCH',
        body,
      }),
    }),
    exchangeToken: builder.mutation<ExchangeTokenResponse, ExchangeTokenRequest>({
      query: (body) => ({
        url: '/api/v1/auth/sessions',
        method: 'POST',
        body,
      }),
    }),
    getMe: builder.query<UserMeResponse, void>({
      query: () => ({
        url: '/api/v1/auth/account',
        method: 'GET',
      }),
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/api/v1/auth/session',
        method: 'DELETE',
      }),
    }),
    getSites: builder.query<Site[], void>({
      query: () => ({
        url: '/api/v1/auth/sites',
        method: 'GET',
      }),
    }),
    setActiveSite: builder.mutation<void, SetActiveSiteRequest>({
      query: (body) => ({
        url: '/api/v1/auth/site',
        method: 'POST',
        body,
      }),
    }),
    createOrganization: builder.mutation<CreateOrganizationResponse, CreateOrganizationRequest>({
      query: (body) => ({
        url: '/api/v1/admin/organizations',
        method: 'POST',
        body,
      }),
    }),
    getMyOrganizations: builder.query<MyOrganization[], void>({
      query: () => ({
        url: '/api/v1/me/organizations',
        method: 'GET',
      }),
    }),
    getMyInvitations: builder.query<MyInvitation[], void>({
      query: () => ({
        url: '/api/v1/me/invitations',
        method: 'GET',
      }),
    }),
    acceptInvitation: builder.mutation<void, AcceptInvitationRequest>({
      query: ({ invitation_token }) => ({
        url: `/api/v1/me/invitations/${invitation_token}/accept`,
        method: 'POST',
      }),
    }),
    declineInvitation: builder.mutation<void, string>({
      query: (invitation_id) => ({
        url: `/api/v1/me/invitations/${invitation_id}`,
        method: 'DELETE',
      }),
    }),
    getRoles: builder.query<Array<{ value: string; name: string; label: string }>, void>({
      query: () => ({
        url: '/api/v1/auth/roles',
        method: 'GET',
      }),
      transformResponse: (response: any): Array<{ value: string; name: string; label: string }> => {
        // Debug: log the response to see its structure
        
        // Extract tenant_roles from response
        let tenantRoles: Array<{ value: string; name: string; label: string }> = [];
        
        // Try different response structures
        if (response?.tenant_roles && Array.isArray(response.tenant_roles)) {
          // Case 1: response.tenant_roles exists
          tenantRoles = response.tenant_roles;
        } else if (Array.isArray(response)) {
          // Case 2: Response is directly an array
          // Check if it's an array of role objects or an array containing tenant_roles
          if (response.length > 0 && response[0]?.value) {
            // It's already an array of role objects
            tenantRoles = response;
          } else {
            // Look for tenant_roles in the array
            const tenantRolesObj = response.find((item: any) => item.tenant_roles);
            if (tenantRolesObj?.tenant_roles && Array.isArray(tenantRolesObj.tenant_roles)) {
              tenantRoles = tenantRolesObj.tenant_roles;
            }
          }
        } else if (response && typeof response === 'object') {
          // Case 3: Check all keys for tenant_roles
          for (const key in response) {
            if (key === 'tenant_roles' && Array.isArray(response[key])) {
              tenantRoles = response[key];
              break;
            }
          }
        }
        
        
        // Filter to only show tenant_admin and tenant_member
        const allowedValues = ['tenant_admin', 'tenant_member'];
        const filteredRoles = tenantRoles.filter((role: any) => {
          const roleValue = role?.value || role?.id || role;
          return typeof roleValue === 'string' && allowedValues.includes(roleValue);
        });
        
        
        // If no roles found, return default roles
        if (filteredRoles.length === 0) {
          console.warn('No roles found in response, using defaults');
          return [
            { value: 'tenant_admin', name: 'TENANT_ADMIN', label: 'Tenant Admin' },
            { value: 'tenant_member', name: 'TENANT_MEMBER', label: 'Tenant Member' },
          ];
        }
        
        return filteredRoles;
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useExchangeTokenMutation,
  useGetMeQuery,
  useLazyGetMeQuery,
  useUpdateAccountMutation,
  useLogoutMutation,
  useGetSitesQuery,
  useSetActiveSiteMutation,
  useCreateOrganizationMutation,
  useGetMyOrganizationsQuery,
  useGetMyInvitationsQuery,
  useAcceptInvitationMutation,
  useDeclineInvitationMutation,
  useGetRolesQuery,
  endpoints: authEndpoints,
} = authApi;


